import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CCardFooter,
    CForm, CFormSelect, CFormFeedback, CSpinner,
    CTable, CTableRow, CTableBody, CTableDataCell, CRow, CCol, CBadge
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import ToastNotification from 'components/globals/ToastNotification';
import { triggerAlert } from 'components/globals/AlertNotification';

// -------------------- Group definitions --------------------
const group1_questions = [
    ['g1_1', '1 - Imagem da STREAM no mercado/sociedade'],
    ['g1_2', '2 - Papel e desempenho global da STREAM na sociedade'],
    ['g1_3', '3 - Nível de envolvimento/contributo dos colaboradores para a missão e imagem'],
    ['g1_4', '4 - Igualdade de Oportunidades (carreira, formação e promoção)'],
];
const group1_options = [
    'Muito negativa/Inexistente', 'Negativa/Pouca', 'Ligeiramente negativa/Algumas',
    'Positiva/Suficiente', 'Muito Positiva/Bastante', 'Excelente/Muitas'
];

const group2_questions = [
    ['g2_1', '1 - Recompensa esforços individuais'],
    ['g2_2', '2 - Recompensa esforços de grupo'],
    ['g2_3', '3 - Estabelece objetivos individuais e de grupo'],
    ['g2_4', '4 - Funcionamento do sistema de avaliação de desempenho'],
    ['g2_5', '5 - Aptidão para liderar a organização'],
    ['g2_6', '6 - Aptidão para comunicar'],
    ['g2_7', '7 - Aptidão para motivar e estimular a iniciativa'],
    ['g2_8', '8 - Adequação do tratamento às pessoas/situações'],
    ['g2_9', '9 - Delegação de competências e responsabilidades'],
    ['g2_10', '10 - Gestão de conflitos e problemas'],
    ['g2_11', '11 - Conciliação trabalho/vida pessoal'],
    ['g2_12', '12 - Condições (salário e outros benefícios)'],
];
const group2_options = ['Muito negativa', 'Negativa', 'Ligeiramente negativa', 'Positiva', 'Muito Positiva', 'Excelente'];

const group3_questions = [
    ['g3_1', '1 - Organização dos processos de trabalho'],
    ['g3_2', '2 - Integração entre os vários departamentos'],
];
const group3_options = ['Muito má/Inexistente', 'Má/Fraca', 'Insuficiente', 'Boa/Suficiente', 'Muito Boa', 'Excelente'];

const group4_questions = [
    ['g4_1', '1 - Mecanismos de consulta e diálogo'],
    ['g4_2', '2 - Plano de carreira disponibilizado'],
    ['g4_3', '3 - Oportunidades criadas'],
    ['g4_4', '4 - Política de Gestão de Recursos Humanos'],
];
const group4_options = ['Inexistente', 'Muito Insuficiente', 'Insuficiente', 'Suficiente', 'Muito Bom', 'Excelente'];

// Codes (1..N) for DB; we’ll send 1-based codes
const group5_catalog = [
    'Bom ambiente de trabalho',
    'Boa organização pessoal/Disciplina',
    'Auto-motivação',
    'Incentivo e reconhecimento das Chefias',
    'Objetivos e diversidade de tarefas',
    'Inovação',
    'Equilíbrio vida pessoal/profissional',
    'Incentivos monetários',
    'Outros',
];

const group6_questions = [
    ['g6_1', '1 - A sua produtividade na função que desempenha?'],
    ['g6_2', '2 - Faz a diferença pela excelência no seu desempenho?'],
    ['g6_3', '3 - Sente-se reconhecido pelo mérito?'],
    ['g6_4', '4 - Sente-se respeitado como indivíduo e pelo trabalho?'],
    ['g6_5', '5 - A organização mostra confiança no seu trabalho?'],
    ['g6_6', '6 - A organização permite desenvolver-se (pessoal/profissional)?'],
    ['g6_7', '7 - Permite aprender continuamente?'],
    ['g6_8', '8 - Permite ter novos desafios?'],
    ['g6_9', '9 - Tem autonomia e responsabilidade?'],
    ['g6_10', '10 - Permite ser empreendedor e pró-ativo?'],
    ['g6_11', '11 - Permite fazer o que gosta?'],
    ['g6_12', '12 - Envolvimento com os valores da organização?'],
    ['g6_13', '13 - Tem o apoio da gestão?'],
    ['g6_14', '14 - Equilíbrio trabalho/vida pessoal?'],
    ['g6_15', '15 - Condições financeiras justas?'],
    ['g6_16', '16 - Flexibilidade aumenta a produtividade?'],
    ['g6_17', '17 - Bom ambiente de trabalho?'],
    ['g6_18', '18 - Colegas envolvidos/competentes?'],
    ['g6_19', '19 - Bom espírito de equipa?'],
    ['g6_20', '20 - Existe humor no dia a dia?'],
    ['g6_21', '21 - Comunicação é fácil?'],
    ['g6_22', '22 - Bons meios e ferramentas?'],
    ['g6_23', '23 - Função é útil para a organização?'],
    ['g6_24', '24 - Chefia confia em si?'],
    ['g6_25', '25 - Tem apoio da chefia quando necessário?'],
    ['g6_26', '26 - É feliz na STREAM?'],
    ['g6_27', '27 - Feliz com a função?'],
    ['g6_28', '28 - Vida perto do ideal?'],
];
const group6_options = [
    'Muito Insatisfeito/Nunca', 'Insatisfeito/Raramente', 'Pouco Insatisfeito/Pontualmente',
    'Satisfeito/Bastante', 'Muito Satisfeito/Quase sempre', 'Muitíssimo Satisfeito/Claramente'
];

// -------------------- Component --------------------
const HappinessSurvey = () => {
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const token = localStorage.getItem('access_token');
    const decodedToken = token ? jwtDecode(token) : {};
    const is_director = decodedToken?.is_director;

    // Initialize empty state similar to SST page (strings, then convert on submit)
    const buildEmptyFormData = () => {
        const init = {};
        [...group1_questions, ...group2_questions, ...group3_questions, ...group4_questions, ...group6_questions]
            .forEach(([key]) => { init[key] = ''; }); // '' | '1'..'6'
        init.g5 = []; // array of integer codes (1..N), max 2
        init.concluded = false;
        return init;
    };

    const fetchSurvey = async () => {
        if (isNew) {
            setFormData(buildEmptyFormData());
            return;
        }
        try {
            const { data } = await apiClient.get(`/happiness-surveys/${id}/`);
            // Expect backend to return integers for g*_* and two fields or array for g5
            // Normalize to UI format:
            const ui = buildEmptyFormData();

            // Map numeric answers to strings
            Object.keys(ui).forEach((k) => {
                if (k === 'g5' || k === 'concluded') return;
                if (data[k] === null || data[k] === undefined) ui[k] = '';
                else ui[k] = String(data[k]); // '1'..'6'
            });

            // Grupo 5: support either array [1,2] or fields g5_op1/g5_op2
            if (Array.isArray(data.g5)) {
                ui.g5 = data.g5.slice(0, 2).map(Number);
            } else {
                const o1 = data.g5_op1 ? Number(data.g5_op1) : null;
                const o2 = data.g5_op2 ? Number(data.g5_op2) : null;
                ui.g5 = [o1, o2].filter((v) => !!v);
            }

            ui.concluded = !!data.concluded;
            setFormData(ui);
        } catch (error) {
            triggerAlert('Inquérito não foi encontrado', 'error');
            navigate('/inqueritos-felicidade');
        }
    };

    useEffect(() => {
        fetchSurvey();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, location.pathname]);

    const isConcluded = !!formData?.concluded;

    const handleSelectChange = (e) => {
        const { name, value } = e.target; // '' | '1'..'6'
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => {
            const updated = { ...prev };
            delete updated[name];
            return updated;
        });
    };

    const handleG5Toggle = (code) => {
        setFormData((prev) => {
            const current = new Set(prev.g5 || []);
            if (current.has(code)) {
                current.delete(code);
            } else {
                if (current.size >= 2) return prev; // block >2
                current.add(code);
            }
            const next = Array.from(current).sort((a, b) => a - b);
            return { ...prev, g5: next };
        });
        setFieldErrors((prev) => {
            const updated = { ...prev };
            delete updated.g5;
            return updated;
        });
    };

    // Build API payload:
    // - g*_*: convert '1'..'6' -> int, omit if ''
    // - g5: send g5_op1/g5_op2 (or change here to send array if your API expects it)
    const buildPayload = () => {
        const payload = {};
        [...group1_questions, ...group2_questions, ...group3_questions, ...group4_questions, ...group6_questions]
            .forEach(([key]) => {
                const v = formData[key];
                if (v !== '') payload[key] = parseInt(v, 10);
            });

        // Grupo5: two nullable fields with codes 1..N
        const [o1, o2] = formData.g5 || [];
        if (o1) payload.g5_op1 = o1;
        if (o2) payload.g5_op2 = o2;

        return payload;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        setSubmitting(true);
        try {
            const payload = buildPayload();

            if (isNew) {
                await apiClient.post('/happiness-surveys/', payload);
                triggerAlert('Inquérito criado com sucesso.', 'success');
            } else {
                await apiClient.put(`/happiness-surveys/${id}/`, payload);
                triggerAlert('Inquérito submetido com sucesso.', 'success');
            }
            navigate('/inqueritos-felicidade');
        } catch (error) {
            const errors = error.response?.data;
            if (errors && typeof errors === 'object') {
                setFieldErrors(errors);
                // Scroll to first invalid control
                setTimeout(() => {
                    const firstInvalid = document.querySelector('select.is-invalid, input.is-invalid');
                    if (firstInvalid) {
                        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        firstInvalid.focus();
                    }
                }, 50);
            } else {
                setFieldErrors({});
            }
            triggerAlert(isNew ? 'Erro ao criar o inquérito.' : 'Erro ao submeter o inquérito.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (!formData) {
        return (
            <div className="text-center my-5">
                <CSpinner />
            </div>
        );
    }

    const renderLikertTable = (title, questions, options) => (
        <CCard className="mb-4">
            <CCardHeader><h5 className="mb-0">{title}</h5></CCardHeader>
            <CCardBody>
                <CTable responsive hover>
                    <CTableBody>
                        {questions.map(([key, label]) => {
                            const selectId = `hs-${key}`;
                            const invalid = !!fieldErrors[key];
                            return (
                                <CTableRow key={key}>
                                    <CTableDataCell className="col-9">
                                        <label htmlFor={selectId}>{label}</label>
                                    </CTableDataCell>
                                    <CTableDataCell className="col-3">
                                        <CFormSelect
                                            id={selectId}
                                            name={key}
                                            value={formData[key] ?? ''}
                                            onChange={handleSelectChange}
                                            disabled={isConcluded}
                                            invalid={invalid}
                                        >
                                            <option value="">Selecione...</option>
                                            {options.map((opt, idx) => (
                                                <option key={idx} value={String(idx + 1)}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                        {invalid && (
                                            <CFormFeedback invalid>
                                                {Array.isArray(fieldErrors[key]) ? fieldErrors[key][0] : String(fieldErrors[key])}
                                            </CFormFeedback>
                                        )}
                                    </CTableDataCell>
                                </CTableRow>
                            );
                        })}
                    </CTableBody>
                </CTable>
            </CCardBody>
        </CCard>
    );

    const renderGroup5 = () => (
        <CCard className="mb-4">
            <CCardHeader>
                <h5 className="mb-1">Grupo 5: FELICIDADE </h5>
                <small>Selecione no máximo <strong>2</strong> opções</small>
            </CCardHeader>
            <CCardBody>
                <CTable responsive hover>
                    <CTableBody>
                        {group5_catalog.map((label, i) => {
                            const code = i + 1; // 1..N
                            const checked = (formData.g5 || []).includes(code);
                            const disabled = isConcluded || (!checked && (formData.g5 || []).length >= 2);
                            const invalid = !!fieldErrors.g5;
                            return (
                                <CTableRow key={code}>
                                    <CTableDataCell className="col-10">
                                        <label htmlFor={`g5-${code}`} className="mb-0">{label}</label>
                                    </CTableDataCell>
                                    <CTableDataCell className="col-2">
                                        <input
                                            type="checkbox"
                                            id={`g5-${code}`}
                                            className={`form-check-input ${invalid ? 'is-invalid' : ''}`}
                                            checked={checked}
                                            disabled={disabled}
                                            onChange={() => handleG5Toggle(code)}
                                        />
                                    </CTableDataCell>
                                </CTableRow>
                            );
                        })}
                    </CTableBody>
                </CTable>
                {!!fieldErrors.g5 && (
                    <div className="invalid-feedback d-block">
                        {Array.isArray(fieldErrors.g5) ? fieldErrors.g5[0] : String(fieldErrors.g5)}
                    </div>
                )}
                <div className="mt-2">
                    <small className="text-muted">
                        Selecionadas: {(formData.g5 || []).length} / 2
                    </small>
                </div>
            </CCardBody>
        </CCard>
    );

    return (
        <div className="container mt-4">
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard className="mb-4">
                <CCardHeader>
                    <h4 className="mb-0">Inquérito de Felicidade</h4>
                </CCardHeader>

                <CForm onSubmit={handleSubmit}>
                    <CCardBody>
                        {/* Group 1 */}
                        {renderLikertTable('Grupo 1: CREDIBILIDADE/IMAGEM DA STREAM NO MERCADO/SOCIEDADE', group1_questions, group1_options)}

                        {/* Group 2 */}
                        {renderLikertTable('Grupo 2: EQUIPA DE GESTÃO E GESTÃO ESTRATÉGICA DA STREAM', group2_questions, group2_options)}

                        {/* Group 3 */}
                        {renderLikertTable('Grupo 3: PROCESSOS DE TRABALHO', group3_questions, group3_options)}

                        {/* Group 4 */}
                        {renderLikertTable('Grupo 4: CARREIRA', group4_questions, group4_options)}

                        {/* Group 5 (top 2) */}
                        {renderGroup5()}

                        {/* Group 6 */}
                        {renderLikertTable('Tendo por base a sua perceção avalie os seguintes pontos', group6_questions, group6_options)}
                    </CCardBody>

                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" type="button" onClick={() => navigate('/inqueritos-felicidade')}>
                            Cancelar
                        </CButton>
                        {!isConcluded && (
                            <CButton color="success" className="text-white" type="submit" disabled={submitting}>
                                {submitting ? <CSpinner size="sm" /> : 'Submeter'}
                            </CButton>
                        )}
                    </CCardFooter>
                </CForm>
            </CCard>
        </div>
    );
};

export default HappinessSurvey;
